/* eslint-disable jsx-a11y/label-has-associated-control */
import { CostCenter, FwmConstants } from '@portal/portal-core';
import { fjModalSizes } from 'components/primitives';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { useAppDispatch, useAppSelector } from 'store/hooks';

import { CloudAdminActions } from '../../../../../../actions';
import { AdminClientActions } from '../../../../../../ducks/admin-client';
import {
  allCurrentSharedComputersSelector,
  currentProductAccessSelector,
} from '../../../../../../selectors';
import { AlertMessage, Checkbox, DatePicker, Input, Modal } from '../../../../../styled-primitives';

import { addYears } from '../../../../../../utils/date-utils';
import { getValidationErrors, selectTextFile } from '../utils';

const {
  END_DATE_YEARS_DEFAULT,
  numClientsMin,
  numConnectionsMin,
  THRESHOLD_DAYS_DEFAULT,
  THRESHOLD_DAYS_MAX,
} = FwmConstants;

const hotDate = new Date();

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${({ theme }) => theme.grid.units(0.5)};
`;

const InputRow = styled.div`
  display: inherit;
  flex-direction: row;
  gap: ${({ theme }) => theme.grid.units(0.5)};
`;

const InputContainer = styled.div`
  display: inherit;
  flex-direction: column;
  flex-grow: 1;
`;

const Label = styled.label`
  display: inherit;
  margin-bottom: 2px;
  color: ${({ theme: { colors } }) => colors.text};
`;

const InputError = styled(AlertMessage).attrs({
  noIcon: true,
})<{ width?: number }>``;

const DeviceFingerprint = styled.textarea`
  resize: none;
  cursor: alias;
`;

// Date fields start as ISO strings (defaultState) and are replaced with the
// DatePicker's `Date | null` on change.
type DateValue = string | Date | null;

interface FormState {
  label: string;
  licenseNumber: string;
  fingerprint: string;
  startDate: DateValue;
  endDate: DateValue;
  contactLabel: string;
  numConnections: number;
  numClients: number;
  autoReview: boolean;
  docManage: boolean;
  touchedInputs: Record<string, boolean>;
  thresholdDays: number;
}

// Each validation error is a single-field map of field name -> message.
type ValidationError = Record<string, string>;

const defaultState: FormState = {
  label: '',
  licenseNumber: '',
  fingerprint: '',
  startDate: hotDate.toISOString(),
  endDate: addYears(hotDate, END_DATE_YEARS_DEFAULT).toISOString(),
  contactLabel: '',
  numConnections: numConnectionsMin,
  numClients: numClientsMin,
  autoReview: false,
  docManage: false,
  touchedInputs: {},
  thresholdDays: THRESHOLD_DAYS_DEFAULT,
};

const MOUNT_TOUCHED: Record<string, boolean> = {
  label: true,
  hwa: true,
  fingerprint: true,
  endDate: true,
  startDate: true,
  contactLabel: true,
};

const ADD_TOUCHED: Record<string, boolean> = {
  label: true,
  licenseNumber: true,
  fingerprint: true,
  endDate: true,
  startDate: true,
  contactLabel: true,
  numConnections: true,
  numClients: true,
  autoReview: true,
  docManage: true,
  thresholdDays: true,
};

const NewFacsWorkflowManagerLicenseModal = (): React.ReactElement | null => {
  const userAdminState = useAppSelector((state) => state.userAdmin);
  // `currentProductAccessSelector` reads an untyped redux slice; resolves as `any`.
  const productAccess = useAppSelector(currentProductAccessSelector);
  const sharedComputerState = useAppSelector(allCurrentSharedComputersSelector);

  const dispatch = useAppDispatch();
  const hideModal = (): void => {
    dispatch(CloudAdminActions.hideModal());
  };
  const addSharedComputer = (
    costCenterId: string,
    productAccessId: string,
    sharedComputerObj: Record<string, unknown>,
    productName: string,
  ): void => {
    dispatch(
      AdminClientActions.reqUserAdminAddSharedComputer(
        costCenterId,
        productAccessId,
        sharedComputerObj,
        productName,
      ),
    );
  };

  const [form, setForm] = useState<FormState>(defaultState);
  // `window.setTimeout` returns a numeric handle in the browser.
  const validationTimeout = useRef<number | undefined>(undefined);

  // `form` holds dates as ISO strings until the DatePicker replaces them with
  // `Date | null`; getValidationErrors compares them loosely, so pass through.
  const validationErrors = (): ValidationError[] =>
    getValidationErrors(
      form as unknown as Parameters<typeof getValidationErrors>[0],
      sharedComputerState,
    );
  const isValid = (): boolean => !validationErrors().length;

  const handleValueChange = (change: Partial<FormState>): void =>
    setForm((prev) => ({ ...prev, ...change }));
  const handleCheckboxChange = (
    change: React.ChangeEvent<HTMLInputElement>,
    field: 'autoReview' | 'docManage',
  ): void => setForm((prev) => ({ ...prev, [field]: change.target.checked }));

  const handleCloseModal = (): void => {
    setForm(defaultState);
    hideModal();
  };

  // check validity on open to indicate required fields; clear the touch-debounce
  // timeout on unmount (class componentDidMount / componentWillUnmount)
  useEffect(() => {
    if (validationErrors().length) {
      setForm((prev) => ({ ...prev, touchedInputs: MOUNT_TOUCHED }));
    }
    return () => clearTimeout(validationTimeout.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // close the modal once a shared computer has been added (class componentDidUpdate)
  const prevSharedComputerState = useRef(sharedComputerState);
  useEffect(() => {
    if (prevSharedComputerState.current.length < sharedComputerState.length) {
      handleCloseModal();
    }
    prevSharedComputerState.current = sharedComputerState;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedComputerState]);

  const renderErrorByField = (field: string, width?: number): React.ReactElement | null => {
    const error = validationErrors().find((msg) => Object.keys(msg)[0] === field);
    if (error && error[field] && form.touchedInputs[field]) {
      return <InputError width={width}>{error[field]}</InputError>;
    }
    return null;
  };

  const handleAdd = (): void => {
    if (!isValid()) {
      setForm((prev) => ({ ...prev, touchedInputs: ADD_TOUCHED }));
      return;
    }
    const { costCenter } = userAdminState;
    const {
      label,
      licenseNumber,
      fingerprint,
      startDate,
      endDate,
      contactLabel,
      numConnections,
      numClients,
      autoReview,
      docManage,
      thresholdDays,
    } = form;

    const sharedComputerObj: Record<string, unknown> = {
      label,
      identifiers: [
        {
          type: CostCenter.SHARED_COMPUTER_IDENTIFIER_TYPES.fwmEncryptedFingerprint,
          value: fingerprint,
        },
      ],
      startDate,
      endDate,
      contactLabel,
      status: 'active',
      features: {
        numConnections,
        numClients,
        licenseNumber,
        autoReview,
        docManage,
        thresholdDays,
      },
    };

    addSharedComputer(
      costCenter!.id as string,
      productAccess.id,
      sharedComputerObj,
      productAccess.name,
    );
    handleCloseModal();
  };

  const handleImport = async (): Promise<void> =>
    selectTextFile('*.txt').then((file: string) =>
      setForm((prev) => ({ ...prev, fingerprint: file })),
    );

  const touchField = (field: string): void => {
    if (!form.touchedInputs[field]) {
      // Timeout keeps this setState() from preventing handleCloseModal()
      validationTimeout.current = window.setTimeout(
        () =>
          setForm((prev) => ({
            ...prev,
            touchedInputs: { ...prev.touchedInputs, [field]: true },
          })),
        100,
      );
    }
  };

  const handleValueChangeAndTouch = (change: Partial<FormState>): void => {
    handleValueChange(change);
    Object.keys(change).forEach(touchField);
  };

  const { costCenter } = userAdminState;
  if (!costCenter) {
    return null;
  }

  return (
    <Modal
      size={fjModalSizes.TALL}
      onRequestClose={handleCloseModal}
      title="Add new FACS Workflow Manager License"
      cancel={{
        text: 'Cancel',
        callback: handleCloseModal,
      }}
      accept={{
        text: 'Add',
        disabled: !isValid(),
        callback: handleAdd,
      }}
      importFile={{
        text: 'Import Device Fingerprint',
        callback: handleImport,
      }}
    >
      <FormContainer>
        <InputRow>
          <InputContainer>
            <Label>Site Name</Label>
            <Input
              placeholder="Enter Label"
              onChange={(label: string) => handleValueChange({ label })}
              value={form.label}
              role="cell"
              tabIndex={0}
              onBlur={() => touchField('label')}
            />
            {renderErrorByField('label', 14)}
          </InputContainer>
          <InputContainer>
            <Label>Contact Label</Label>
            <Input
              placeholder="Enter Contact Label"
              onChange={(contactLabel: string) => handleValueChange({ contactLabel })}
              value={form.contactLabel}
              role="button"
              tabIndex={0}
              onBlur={() => touchField('contactLabel')}
            />
            {renderErrorByField('contactLabel', 14)}
          </InputContainer>
        </InputRow>
        <InputRow>
          <InputContainer>
            <Label>Activation Date</Label>
            <DatePicker
              selected={form.startDate}
              onChange={(startDate) => handleValueChangeAndTouch({ startDate })}
              // `placeholderText`/`onBlur` are forwarded by DatePicker to
              // react-datepicker via its rest spread but are absent from the typed
              // DatePickerProps; spread them so assignment is structural-checked.
              {...{
                placeholderText: 'Click to select a date',
                onBlur: () => touchField('startDate'),
              }}
            />
            {renderErrorByField('startDate', 14)}
          </InputContainer>
          <InputContainer>
            <Label>Deactivation Date</Label>
            <DatePicker
              selected={form.endDate}
              disabled={!form.startDate}
              onChange={(endDate) => handleValueChangeAndTouch({ endDate })}
              minDate={form.startDate}
              time={DatePicker.END_OF_DAY}
              {...{
                placeholderText: 'Click to select a date',
                onBlur: () => touchField('endDate'),
              }}
            />
            {renderErrorByField('endDate')}
          </InputContainer>
          <InputContainer>
            <Label>Threshold Days</Label>
            <Input
              placeholder={form.thresholdDays}
              onChange={(thresholdDays: string) =>
                handleValueChange({
                  thresholdDays: parseInt(thresholdDays, 10) || 0,
                })
              }
              validator={(value: number) => value <= THRESHOLD_DAYS_MAX}
              getValidationMessage={`Max allowed value: ${THRESHOLD_DAYS_MAX}`}
              value={form.thresholdDays}
              role="cell"
              tabIndex={0}
              onBlur={() => touchField('thresholdDays')}
            />
            {renderErrorByField('thresholdDays', 14)}
          </InputContainer>
        </InputRow>
        <InputRow>
          <InputContainer>
            {/* TODO add tooltip */}
            <Label>Connections</Label>
            <Input
              placeholder={form.numConnections}
              onChange={(numConnections: string) =>
                handleValueChange({ numConnections: parseInt(numConnections, 10) || 0 })
              }
              value={form.numConnections}
              role="cell"
              tabIndex={0}
              onBlur={() => touchField('numConnections')}
            />
            {renderErrorByField('numConnections', 14)}
          </InputContainer>
          <InputContainer>
            {/* TODO add tooltip */}
            <Label>Clients</Label>
            <Input
              placeholder={form.numClients}
              onChange={(numClients: string) =>
                handleValueChange({ numClients: parseInt(numClients, 10) || 0 })
              }
              value={form.numClients}
              role="cell"
              tabIndex={0}
              onBlur={() => touchField('numClients')}
            />
            {renderErrorByField('numClients', 14)}
          </InputContainer>
          <InputContainer>
            {/* TODO add tooltip */}
            <Label>Auto Review</Label>
            <Checkbox
              name="autoReview"
              onChange={(v) => handleCheckboxChange(v, 'autoReview')}
              checked={form.autoReview}
              // `onBlur` is forwarded by FjCheckbox via its rest spread but is
              // absent from CheckboxProps; spread so it is structural-checked.
              {...{ onBlur: () => touchField('autoReview') }}
            />
          </InputContainer>
          <InputContainer>
            {/* TODO add tooltip */}
            <Label>Doc Manage</Label>
            <Checkbox
              name="docManage"
              onChange={(v) => handleCheckboxChange(v, 'docManage')}
              checked={form.docManage}
              {...{ onBlur: () => touchField('docManage') }}
            />
          </InputContainer>
        </InputRow>
        <InputRow>
          <InputContainer>
            <Label>Device Fingerprint</Label>
            <DeviceFingerprint
              placeholder="Click to import device fingerprint"
              value={form.fingerprint}
              onClick={handleImport}
              tabIndex={0}
              onBlur={() => touchField('fingerprint')}
              readOnly
              rows={17}
            />
            {renderErrorByField('fingerprint', 14)}
          </InputContainer>
        </InputRow>
      </FormContainer>
    </Modal>
  );
};

export default NewFacsWorkflowManagerLicenseModal;
